import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ButtonGroup,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Web as WebIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useAuditLogs, AuditEntryRequestDto } from "../../hooks/useAuditLogs";
import { useTenantHeader } from "../../hooks/useTenantHeader";
import { useTranslation } from "react-i18next";
import EnhancedTable from "../common/EnhancedTable";

const AuditLogManagement: React.FC = () => {
  const { t } = useTranslation();
  const { tenantLoading } = useTenantHeader();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const requestDto: AuditEntryRequestDto = {
    page,
    size: rowsPerPage,
  };

  const {
    data: auditData,
    isLoading,
    error,
    refetch,
  } = useAuditLogs(requestDto);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const handleViewDetails = (log: any) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const handleDateFilterChange = (filter: string) => {
    setDateFilter(filter);
    setPage(0); // Reset to first page when filter changes

    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = null;

    switch (filter) {
      case "lastWeek":
        const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        start = lastWeek;
        end = now;
        break;
      case "lastMonth":
        const lastMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        start = lastMonth;
        end = now;
        break;
      case "custom":
        // Keep current custom dates
        break;
      default:
        // 'all' - clear dates
        start = null;
        end = null;
        break;
    }

    setStartDate(start);
    setEndDate(end);
  };

  const handleCustomDateChange = (type: "start" | "end", value: Date | null) => {
    if (type === "start") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    setDateFilter("custom");
  };

  const getActionIcon = (action: string) => {
    const actionLower = action.toLowerCase();
    if (actionLower.includes("login")) return <SecurityIcon />;
    if (actionLower.includes("create")) return <CheckCircleIcon />;
    if (actionLower.includes("update")) return <InfoIcon />;
    if (actionLower.includes("delete")) return <ErrorIcon />;
    if (actionLower.includes("download")) return <ComputerIcon />;
    return <PersonIcon />;
  };

  const getActionColor = (action: string, success: boolean) => {
    if (!success) return "error";
    const actionLower = action.toLowerCase();
    if (actionLower.includes("login")) return "success";
    if (actionLower.includes("create")) return "primary";
    if (actionLower.includes("update")) return "info";
    if (actionLower.includes("delete")) return "warning";
    return "default";
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  // Helper function to parse date in dd/MM/yyyy HH:mm:ss format with Asia/Kolkata timezone
  const parseFormattedDate = (dateString: string): Date => {
    const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/;
    const match = dateString.match(datePattern);
    
    if (match) {
      const [, day, month, year, hour, minute, second] = match;
      // Create date in local timezone (the backend already provides time in Asia/Kolkata)
      const date = new Date(
        parseInt(year),
        parseInt(month) - 1, // Month is 0-indexed in JavaScript
        parseInt(day),
        parseInt(hour),
        parseInt(minute),
        parseInt(second)
      );
      return date;
    }
    
    // Fallback to standard date parsing
    return new Date(dateString);
  };

  const formatLocalDateTime = (localDateTime: string | number[] | number) => {
    if (!localDateTime) return "N/A";
    
    // Handle formatted string format (dd/MM/yyyy HH:mm:ss) from @JsonFormat
    if (typeof localDateTime === 'string') {
      try {
        const date = parseFormattedDate(localDateTime);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString();
        }
      } catch (error) {
      }
    }
    
    // Handle Unix timestamp format - number with seconds and nanoseconds
    if (typeof localDateTime === 'number') {
      try {
        // Convert Unix timestamp to milliseconds (multiply by 1000)
        const date = new Date(localDateTime * 1000);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString();
        }
      } catch (error) {
      }
    }
    
    // Handle array format (old format) for backward compatibility
    if (Array.isArray(localDateTime) && localDateTime.length === 7) {
      const [year, month, day, hour, minute, second, nanosecond] = localDateTime;
      const date = new Date(year, month - 1, day, hour, minute, second);
      return date.toLocaleString();
    }
    return "N/A";
  };

  // Table columns
  const columns = [
    { id: "user", label: t("user"), minWidth: 150, align: "left" as const },
    { id: "action", label: t("action"), minWidth: 120, align: "left" as const },
    {
      id: "timestamp",
      label: t("timestamp"),
      minWidth: 150,
      align: "left" as const,
    },
    {
      id: "status",
      label: t("status"),
      minWidth: 100,
      align: "center" as const,
    },
    {
      id: "actions",
      label: t("actions"),
      minWidth: 100,
      align: "center" as const,
      sortable: false,
    },
  ];

  // Actions: View Details
  const renderActions = (row: any) => (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        alignItems: "center",
        justifyContent: "center",
        minWidth: "100px",
        flexWrap: "nowrap",
      }}
    >
      <Tooltip title={t("view_details")} arrow placement="top">
        <IconButton
          onClick={() => handleViewDetails(row)}
          size="small"
          sx={{
            color: "primary.main",
            padding: "6px",
            "&:hover": {
              backgroundColor: "primary.light",
              color: "primary.contrastText",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // Filter data based on date range
  const filteredData = (auditData?.content || []).filter((log: any) => {
    if (dateFilter === "all" || (!startDate && !endDate)) {
      return true;
    }

    let logDate: Date;
    
    if ((log as any).localDateTime) {
      // Handle formatted string format (dd/MM/yyyy HH:mm:ss) from @JsonFormat
      if (typeof (log as any).localDateTime === 'string') {
        logDate = parseFormattedDate((log as any).localDateTime);
      }
      // Handle Unix timestamp format - number with seconds and nanoseconds
      else if (typeof (log as any).localDateTime === 'number') {
        logDate = new Date((log as any).localDateTime * 1000);
      } 
      // Handle array format (old format) for backward compatibility
      else if (Array.isArray((log as any).localDateTime) && (log as any).localDateTime.length >= 3) {
        logDate = new Date(
          (log as any).localDateTime[0],
          (log as any).localDateTime[1] - 1,
          (log as any).localDateTime[2]
        );
      } else {
        logDate = new Date();
      }
    } else {
      logDate = new Date(log.timestamp);
    }

    if (startDate && endDate) {
      return logDate >= startDate && logDate <= endDate;
    } else if (startDate) {
      return logDate >= startDate;
    } else if (endDate) {
      return logDate <= endDate;
    }

    return true;
  });

  // Debug: Log the first audit entry to understand the data structure
  if (filteredData.length > 0) {
    const firstLog = filteredData[0] as any; // Type assertion to avoid TypeScript errors
  }

  // Apply search filter to filteredData
  const searchFilteredData = filteredData.filter((log: any) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const userName = log.userName || log.username || "N/A";
    const userRole = log.userRole || "N/A";
    const actionText = log.action || "";
    const statusText = log.status === "PASSED" ? t("success") : t("failed");
    
    return (
      userName.toLowerCase().includes(searchLower) ||
      userRole.toLowerCase().includes(searchLower) ||
      actionText.toLowerCase().includes(searchLower) ||
      statusText.toLowerCase().includes(searchLower)
    );
  });

  // Format data for EnhancedTable
  const formattedData = searchFilteredData.map((log: any) => ({
    ...log,
    // Add searchable text fields
    userName: log.userName || log.username || "N/A",
    userRole: log.userRole || "N/A",
    actionText: log.action,
    statusText: log.status === "PASSED" ? t("success") : t("failed"),
    timestampText: (log as any).localDateTime
      ? formatLocalDateTime((log as any).localDateTime)
      : log.timestamp
      ? formatTimestamp(log.timestamp)
      : "N/A",
    // Display components
    user: (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
          <PersonIcon />
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {log.userName || log.username || "N/A"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {log.userRole || "N/A"}
          </Typography>
        </Box>
      </Box>
    ),
    action: (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {getActionIcon(log.action)}
        <Typography variant="body2">{log.action}</Typography>
      </Box>
    ),
    timestamp: (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <TimeIcon sx={{ fontSize: 16 }} />
        <Typography variant="body2">
          {(log as any).localDateTime
            ? formatLocalDateTime((log as any).localDateTime)
            : log.timestamp
            ? formatTimestamp(log.timestamp)
            : "N/A"}
        </Typography>
      </Box>
    ),
    status: (
      <Chip
        label={log.status === "PASSED" ? t("success") : t("failed")}
        color={log.status === "PASSED" ? "success" : "error"}
        size="small"
        icon={log.status === "PASSED" ? <CheckCircleIcon /> : <ErrorIcon />}
      />
    ),
    actions: renderActions(log),
  }));


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            {t("actions_performed")}
          </Typography>
        </Box>

        {/* Search and Filter Section - matching other management pages layout */}
        <Box
          sx={{ mb: 2, display: "flex", justifyContent: "end", alignItems: "center" }}
        >

          {/* Right: Search + Filter */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {dateFilter === "custom" && (
              <>
                <DatePicker
                  label={t("start_date")}
                  value={startDate}
                  onChange={(newValue) =>
                    handleCustomDateChange("start", newValue)
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { width: 170 }
                    },
                  }}
                />
                <DatePicker
                  label={t("end_date")}
                  value={endDate}
                  onChange={(newValue) =>
                    handleCustomDateChange("end", newValue)
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { width: 170 }
                    },
                  }}
                />
              </>
            )}
          <TextField
              fullWidth
              select
              label={t("date_range")}
              name="dateRange"
              value={dateFilter}
              onChange={(e) => handleDateFilterChange(e.target.value)}
              size="small"
              margin="dense"
              sx={{ backgroundColor: '#f8f9fb', width: 180 }}
              required
              disabled={false}
            >
              <MenuItem value="all">{t("all_logs")}</MenuItem>
              <MenuItem value="lastWeek">{t("last_week")}</MenuItem>
              <MenuItem value="lastMonth">{t("last_month")}</MenuItem>
              <MenuItem value="custom">{t("custom_range")}</MenuItem>
            </TextField>
            <TextField
              size="small"
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: "300px" }}
            />            
          </Box>
        </Box>

        {tenantLoading || isLoading ? (
          <CircularProgress sx={{ display: "block", margin: "20px auto" }} />
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(error as Error).message}
          </Alert>
        ) : null}

        <EnhancedTable
          columns={columns}
          data={formattedData}
          enablePagination={true}
          page={page}
          rowsPerPage={rowsPerPage}
          totalElements={auditData?.totalElements || 0}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          cardProps={{
            elevation: 0,
            sx: { 
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent'
            }
          }}
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SecurityIcon />
            {t("audit_details")}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("username")}
                    secondary={
                      selectedLog.userName || selectedLog.username || "N/A"
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("user_role")}
                    secondary={selectedLog.userRole || "N/A"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {getActionIcon(selectedLog.action)}
                  </ListItemIcon>
                  <ListItemText
                    primary={t("action")}
                    secondary={selectedLog.action}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("status")}
                    secondary={
                      <Chip
                        label={
                          selectedLog.status === "PASSED"
                            ? t("success")
                            : t("failed")
                        }
                        color={
                          selectedLog.status === "PASSED" ? "success" : "error"
                        }
                        size="small"
                      />
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TimeIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("timestamp")}
                    secondary={
                      (selectedLog as any).localDateTime
                        ? formatLocalDateTime((selectedLog as any).localDateTime)
                        : selectedLog.timestamp
                        ? formatTimestamp(selectedLog.timestamp)
                        : "N/A"
                    }
                  />
                </ListItem>
                {selectedLog.methodName && selectedLog.methodName !== "N/A" && (
                  <ListItem>
                    <ListItemIcon>
                      <ComputerIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("method_name")}
                      secondary={selectedLog.methodName}
                    />
                  </ListItem>
                )}
                {selectedLog.entityName && selectedLog.entityName !== "N/A" && (
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("entity_name")}
                      secondary={selectedLog.entityName}
                    />
                  </ListItem>
                )}
                {selectedLog.ipAddress && (
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("ip_address")}
                      secondary={selectedLog.ipAddress}
                    />
                  </ListItem>
                )}
                {selectedLog.userAgent && (
                  <ListItem>
                    <ListItemIcon>
                      <WebIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("user_agent")}
                      secondary={selectedLog.userAgent}
                    />
                  </ListItem>
                )}
              </List>

              {selectedLog.details && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    {t("details")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ bgcolor: "grey.100", p: 2, borderRadius: 1 }}
                  >
                    {selectedLog.details}
                  </Typography>
                </>
              )}

              {selectedLog.errorMessage && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom color="error">
                    {t("error_message")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ bgcolor: "error.light", p: 2, borderRadius: 1 }}
                  >
                    {selectedLog.errorMessage}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>{t("close")}</Button>
        </DialogActions>
        </Dialog>
    </LocalizationProvider>
  );
};

export default AuditLogManagement;
